'use client';

import { Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { PageTitle } from '@/styles/PageTitle';
import { LoadingCard } from '@britishcouncil/react-solas-ors3';

import cn from 'classnames';

import { usePathname } from '@/hooks/useRouter';
import { getFullName } from '@/utils/helpers';
import { Alert, FullScreenLoadingBox } from '@/components';
import { commonAPI } from '@/store';

import { CustomerDetails, S } from '../../components';

type Props = {
  params: {
    customerId: string;
  };
};

const Customer = ({ params: { customerId } }: Props) => {
  const { data: customer, error, isLoading } = commonAPI.useCustomerGetQuery({ id: customerId });

  const currentRoute = usePathname();

  const isActive = (path: string) => currentRoute === `/enrolments${path}`;

  if (isLoading) return <FullScreenLoadingBox />;

  if (error || !customer) {
    return (
      <>
        <Alert type="error" title="Cannot load customer">
          <p>Please try again later</p>
        </Alert>
      </>
    );
  }

  const tabs = [
    {
      key: 'details',
      text: 'Personal details',
      href: `/customer/${customer.id}/details`,
    },
    {
      key: 'identification',
      text: 'Identification',
      href: `/customer/${customer.id}/identification`,
    },
    {
      key: 'sessions',
      text: 'Sessions',
      href: `/customer/${customer.id}/sessions`,
    },
  ];

  return (
    <>
      <header>
        <PageTitle>{getFullName(customer)}</PageTitle>
        <CustomerDetails customer={customer} />
      </header>
      <S.TabsContainer>
        {tabs.map(({ key, href, text }) => (
          <S.TabElement key={key}>
            <S.TabLink className={cn({ active: isActive(href) })} href={href}>
              {text}
            </S.TabLink>
          </S.TabElement>
        ))}
      </S.TabsContainer>
      <S.OutletContainer>
        <Suspense fallback={<LoadingCard />}>
          <Outlet />
        </Suspense>
      </S.OutletContainer>
    </>
  );
};

export default Customer;
